import { Transaction } from '@stellar/stellar-sdk';
import * as React from 'react';
import { useState } from 'react';

import type AccountService from 'services/account.service';
import { mergeChallengeSignatures } from 'services/vault-challenge';

import { ModalProps } from 'types/modal';

import Button from 'basics/buttons/Button';
import CopyButton from 'basics/buttons/CopyButton';
import { ModalWrapper } from 'basics/ModalAtoms';

import {
    Heading,
    Description,
    ManualActions,
    InputLabel,
    SignedInput,
    Footer,
    Status,
    ErrorText,
} from './SignChallengeWithMultisig.styled';
import SignChallengeWithVault from './SignChallengeWithVault';

interface Props {
    tx: Transaction;
    account: AccountService;
    useVault: boolean;
}

const SignChallengeWithMultisig = (props: ModalProps<Props>) => {
    const { params, confirm } = props;
    const { tx, useVault } = params;
    const [signed, setSigned] = useState('');
    const [error, setError] = useState('');
    if (useVault) return <SignChallengeWithVault {...props} />;
    const submit = () => {
        try {
            confirm({ xdr: mergeChallengeSignatures(tx, signed.trim()).toXdr() });
        } catch {
            setError('Paste the signed XDR for this request. The transaction must not be changed.');
        }
    };
    return (
        <ModalWrapper>
            <Heading>Verify your account</Heading>
            <Description>
                Sign this request with your multisig account’s required signers, then paste the
                signed XDR below. No transaction needs to be submitted to the network.
            </Description>
            <ManualActions>
                <a
                    href={`https://lab.stellar.org/transaction/cli-sign?networkPassphrase=${encodeURIComponent(tx.networkPassphrase)}&xdr=${encodeURIComponent(tx.toXdr())}`}
                    target="_blank"
                    rel="noreferrer noopener"
                >
                    Open Stellar Laboratory ↗
                </a>
                <CopyButton text={tx.toXdr()}>Copy XDR</CopyButton>
            </ManualActions>
            <InputLabel>
                Signed transaction
                <SignedInput
                    value={signed}
                    placeholder="Paste signed XDR here"
                    onChange={event => {
                        setSigned(event.target.value);
                        setError('');
                    }}
                />
            </InputLabel>
            {error && <ErrorText role="alert">{error}</ErrorText>}
            <Footer>
                <Status>Your signatures confirm account ownership.</Status>
                <Button disabled={!signed.trim()} onClick={submit}>
                    Submit
                </Button>
            </Footer>
        </ModalWrapper>
    );
};

export default SignChallengeWithMultisig;
